import * as React from 'react';
import { type ChangeEvent, type KeyboardEvent } from 'react';
import * as ReactDOM from 'react-dom';
import { ActionIcon, TextInput } from '@mantine/core';
import { IconSend } from '@tabler/icons-react';
import { useTarget } from '@epihub/web/src/hooks/useTarget';
import * as _ from 'lodash';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';
import { useOnMount } from '@epihub/web/src/hooks/useOnMount';
import { stopPropagation } from '@epihub/web/src/hooks/useStopPropagation';
import { cssFontFamily } from '../css';
import { LottieCircle } from '../Lottie';
import { HTMLConst } from '../../lib/HTMLConst';
import { QuickResponseChips } from '../QuickResponseChips';
import { reactSpringPortal } from '../../lib/HTMLComp';
import { styled, useTheme } from '../../theme/theme';
import { useGlobalStore } from '../../store/useGlobalStore';
import { useSendMessage } from '../../hooks/useSendMessage';
import { useSfyr } from '../../_ctx/sfyr';
import { useTextInputFocusHandler } from './useTextInputFocusHandler';
import { ChatViewMessageBoxAnimationContext } from './ChatViewMessageBoxAnimationContext';

function useToolbarCircleInTarget() {
  const messages = useGlobalStore((store) => store.conversation.conversation.messages);

  const {
    Target: LottieCircleInTarget,
    measureRef: lottieCircleInTargetMeasureRef,
    destroyRef: lottieCircleInTargetDestroyRef,
  } = useTarget(
    LottieCircle,
    HTMLConst.sendButtonTarget,
    React.useCallback((rect: DOMRect) => ({
      top: rect.y - 10,
      left: rect.x - 30,
      width: rect.width + 60,
      transform: 'rotate(-11deg)',
    }), []),
  );

  useOnMount(() => lottieCircleInTargetMeasureRef.current(), { delayMs: 1000 });

  const hasSent = !!_.find(messages, (message) => message.role === 'user');
  React.useEffect(() => {
    if (hasSent) {
      lottieCircleInTargetDestroyRef.current();
    }
  }, [hasSent]);

  return LottieCircleInTarget;
}

type TChatViewInputProps = {
  className?: string;
};

export const ChatViewInput = ({ className }: TChatViewInputProps) => {
  const sfyr = useSfyr();
  const chatInput = useGlobalStore((store) => store.conversation.chatInput);
  const setChatInput = useGlobalStore((store) => store.conversation.setChatInput);

  const theme = useTheme();
  const { chatViewAnimationOriginId, recalculateAnimationOrigin } = React.useContext(
    ChatViewMessageBoxAnimationContext,
  );

  useOnMount(() => {
    recalculateAnimationOrigin();
  });

  const textInputFocusHandler = useTextInputFocusHandler();

  const sendMessage = useSendMessage();
  const globalContextRef = useLatestRef({
    sendMessage,
    chatInput: {
      input: chatInput,
      setInput: setChatInput,
    },
  });

  const onSendMessage = React.useCallback((event: any) => {
    textInputFocusHandler.refocusInput();
    stopPropagation(event);
    if (`${globalContextRef.current.chatInput.input}`.length <= 0) {
      return;
    }

    globalContextRef.current.sendMessage(globalContextRef.current.chatInput.input);
    globalContextRef.current.chatInput.setInput('');
  }, [globalContextRef, globalContextRef]);

  const onEnterPress = React.useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    event.key === 'Enter' && onSendMessage(event);
  }, [onSendMessage]);

  const setInputValue = React.useCallback((event: ChangeEvent<HTMLInputElement>) => {
    return globalContextRef.current.chatInput.setInput(event.target.value);
  }, [globalContextRef]);

  const LottieCircleInTarget = useToolbarCircleInTarget();

  return (
    <Root className={className}>
      <StyledQuickResponseChips
        setInput={setChatInput}
        textInputFocusHandler={textInputFocusHandler}
      />
      <InputContainer>
        <StyledTextField
          autoFocus
          autoComplete='off'
          key={chatViewAnimationOriginId}
          id={chatViewAnimationOriginId}
          ref={textInputFocusHandler.inputRef}
          inputcolor={theme.legacy.colors.positive}
          variant='unstyled'
          type='text'
          size='md'
          value={chatInput}
          onChange={setInputValue}
          onKeyDown={onEnterPress}
          placeholder={sfyr('ui.{lang}.input_placeholder', {
            defaults: {
              'ui.{lang=en}.input_placeholder': 'Ask me anything!',
              'ui.{lang=it}.input_placeholder': 'Chiedimi qualunque cosa!',
            },
          })}
        />
        <>
          {ReactDOM.createPortal(
            (
              <LottieCircleInTarget
                color={[theme.legacy.colors.accent1]}
              />
            ),
            reactSpringPortal,
          )}
          <ActionIcon
            id={HTMLConst.sendButtonTarget}
            p='10px'
            size='xl'
            aria-label='send'
            color='green'
            variant='transparent'
            {...textInputFocusHandler.refocusHoc(onSendMessage)}
          >
            <IconSend />
          </ActionIcon>
        </>
      </InputContainer>
    </Root>
  );
};

const Root = styled.div`
  ${cssFontFamily};
  width: 100%;
`;

const InputContainer = styled.div`
  align-items: center;
  display: flex;
  margin-top: 16px;
`;

const StyledTextField = styled(TextInput)<{ inputcolor: string }>`
  flex: 1;

  .mantine-TextInput-input {
    color: ${({ inputcolor }) => inputcolor};
    border-bottom: 1px solid ${({ inputcolor }) => inputcolor};
    border-radius: 0;

    &::placeholder {
      color: ${({ inputcolor }) => inputcolor};
    }
  }
`;

const StyledQuickResponseChips = styled(QuickResponseChips)`
  margin-bottom: 24px;
`;
